import React from 'react';
import {
  ChipField,
  Loading,
  RecordContextProvider,
  SingleFieldList,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import ArrayDiffField from '../utils/auditTracks/ArrayDiffField';
import ChipStringField from '../../customFields/ChipStringField';
import StatusDiffField from '../utils/auditTracks/StatusDiffField';
import ReferenceDiffField from '../utils/auditTracks/ReferenceDiffField';
import ReferenceArrayDiffField from '../utils/auditTracks/ReferenceArrayDiffField';
import resourceNames from '../../constants/resourceNames';

type IRule = {
  name: string;
  customName: string;
  score: number;
  ruleType: string;
  recommendation: string;
  process: string;
  isAutoConfirmed: boolean;
  createPing: boolean;
  numberOfDaysIgnoreRule: number;
  shouldOffsetDeadline: boolean;
  ruleParameters: string;
  notificationEmails: string[];
  tags: string[];
  signals: string[];
};

const RuleAuditDetails = () => {
  const record = useRecordContext<AuditTrack<IRule>>();
  const { data, isLoading } = useGetOne<AuditTrack<IRule>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const toStringifiedParams = (r: typeof record) => ({
    ...r,
    documentAfter: r.documentAfter
      ? { ...r.documentAfter, ruleParameters: JSON.stringify(r.documentAfter.ruleParameters) }
      : r.documentAfter,
  });

  const auditTracksDiff = auditTrackDiffGenerator<IRule>({
    current: toStringifiedParams(record) as AuditTrack<IRule>,
    previous: !isElementCreated ? toStringifiedParams(data) as AuditTrack<IRule> : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    name: resourceData.name?.hasChanged,
    customName: resourceData.customName?.hasChanged,
    score: resourceData.score?.hasChanged,
    ruleType: resourceData.ruleType?.hasChanged,
    recommendation: resourceData.recommendation?.hasChanged,
    process: resourceData.process?.hasChanged,
    isAutoConfirmed: resourceData.isAutoConfirmed?.hasChanged,
    createPing: resourceData.createPing?.hasChanged,
    numberOfDaysIgnoreRule: resourceData.numberOfDaysIgnoreRule?.hasChanged,
    shouldOffsetDeadline: resourceData.shouldOffsetDeadline?.hasChanged,
    ruleParameters: resourceData.ruleParameters?.hasChanged,
    notificationEmails: resourceData.notificationEmails?.hasChanged,
    tags: resourceData.tags?.hasChanged,
    signals: resourceData.signals?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.name && <TextDiffField source="name" label="Name" />}
          {hasFieldChanges.customName && <TextDiffField source="customName" label="Custom name" />}
          {hasFieldChanges.score && <TextDiffField source="score" label="Score" />}
          {hasFieldChanges.ruleType && <TextDiffField source="ruleType" label="Rule type" />}
          {hasFieldChanges.recommendation && <StatusDiffField source="recommendation" label="Recommendation" />}
          {hasFieldChanges.process && (
            <ReferenceDiffField source="process" label="Process" reference={resourceNames.PROCESSES} />
          )}
          {hasFieldChanges.isAutoConfirmed && <TextDiffField source="isAutoConfirmed" label="Auto confirmed" />}
          {hasFieldChanges.createPing && <TextDiffField source="createPing" label="Create ping" />}

          {hasFieldChanges.numberOfDaysIgnoreRule && (
            <TextDiffField source="numberOfDaysIgnoreRule" label="Days ignore rule" />
          )}

          {hasFieldChanges.shouldOffsetDeadline && (
            <TextDiffField source="shouldOffsetDeadline" label="Offset deadline" />
          )}

          {hasFieldChanges.ruleParameters && (
            <TextDiffField source="ruleParameters" label="Rule parameters" />
          )}

          {hasFieldChanges.notificationEmails && (
            <ArrayDiffField source="notificationEmails" label="Notification emails">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}

          {hasFieldChanges.tags && (
            <ArrayDiffField source="tags" label="Tags">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}

          {hasFieldChanges.signals && (
            <ReferenceArrayDiffField source="signals" label="Signals" reference={resourceNames.SIGNALS}>
              <SingleFieldList linkType={false}>
                <ChipField source="name" size="small" />
              </SingleFieldList>
            </ReferenceArrayDiffField>
          )}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default RuleAuditDetails;
